import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { LoanStatus } from '../cmg-1003/models';
import { SessionQuery } from '../core/state';
import { Cmg1003LoanQuery } from './loan/loan.query';

type FormControlEnabledModel = {
  controlId: string;
  enabledInLoanStatuses: LoanStatus[];
  hasChanged: boolean;
};

@Injectable({ providedIn: 'root' })
export class PostProcessingUrlaUiService {
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);

  overrideSaveLoanDisabled$ = new BehaviorSubject<boolean>(false);

  private readonly _formControls: FormControlEnabledModel[];

  constructor() {
    this._formControls = [
      {
        controlId: 'loanProcessorUserName',
        enabledInLoanStatuses: [LoanStatus.FileReceived],
        hasChanged: false,
      },
    ];
  }

  // TODO - change this to take an array of controlIds
  enableFormControlBasedOnLoanStatus$(controlId: string) {
    return combineLatest({
      isLockedDown: this._loanQuery.isLockedDown$,
      all1003Loaded: this._sessionQuery.select((s) => s.all1003Loaded),
      loanStatus: this._sessionQuery.select((s) => s.loanStatusId),
    }).pipe(
      filter(({ all1003Loaded, loanStatus }) => {
        return !!all1003Loaded && !!loanStatus;
      }),
      debounceTime(100),
      map(({ isLockedDown, loanStatus }) => {
        let isControlEnabled = false;

        this._formControls.forEach((fc) => {
          if (!isLockedDown && fc.controlId === controlId && fc.enabledInLoanStatuses.includes(loanStatus)) {
            isControlEnabled = true;
          }
        });

        return isControlEnabled;
      }),
      distinctUntilChanged(),
    );
  }

  setFormControlHasChanged(controlId: string) {
    const formControl = this._formControls.filter((control) => control.controlId === controlId)?.at(0);

    if (formControl) {
      this._sessionQuery
        .select((s) => s.loanStatusId)
        .pipe(take(1))
        .subscribe((loanStatus) => {
          if (formControl.enabledInLoanStatuses.includes(loanStatus)) {
            formControl.hasChanged = true;
            this.overrideSaveLoanDisabled$.next(true);
          }
        });
    }
  }

  reset() {
    this._formControls.forEach((fc) => {
      fc.hasChanged = false;
    });
    this.overrideSaveLoanDisabled$.next(false);
  }
}
